<template>
  <div class="mt-8 pt-6 border-t">
    <div class="flex items-center justify-between gap-3 mb-3">
      <h3 class="text-lg font-semibold">{{ title }}</h3>
      <button
        v-if="showRefreshButton"
        @click="$emit('refresh')"
        :disabled="loading"
        :class="['px-3 py-1.5 rounded border text-xs', loading ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white hover:bg-gray-50']"
      >
        {{ loading ? loadingButtonLabel : refreshButtonLabel }}
      </button>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">{{ loadingMessage }}</div>
    <div v-else-if="error" class="text-sm text-red-600">{{ error }}</div>
    <div v-else-if="!keyInsights.length && !insightCards.length" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
      {{ noDataMessage }}
    </div>
    <div v-else class="space-y-5">
      <div>
        <div class="text-sm font-semibold mb-2">{{ keyInsightsTitle }}</div>
        <div v-if="!keyInsights.length" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
          {{ keyInsightsEmptyMessage }}
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="card in keyInsights" :key="`insight-key-${card.insight_id || card.headline}`" class="border rounded-lg bg-white p-4">
            <div class="flex items-center justify-between gap-2 mb-1">
              <div class="text-xs uppercase tracking-wide text-gray-500">{{ engagementThemeText(card.theme) }}</div>
              <div class="text-xs text-gray-500">Strength {{ card.strength_score }}</div>
            </div>
            <div class="text-sm font-semibold text-gray-900 mb-2">{{ card.headline }}</div>
            <div class="text-sm text-gray-700 mb-2">{{ card.summary }}</div>
            <div v-if="(card.evidence || []).length" class="mt-3 space-y-1">
              <div v-for="(line, lineIdx) in (card.evidence || []).slice(0, 4)" :key="`insight-key-${card.insight_id || card.headline}-line-${lineIdx}`" class="text-xs text-gray-500">
                {{ line }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="insightCards.length">
        <button type="button" class="w-full flex items-center justify-between text-left text-sm font-semibold mb-2" @click="$emit('toggle-deterministic')">
          <span>{{ deterministicTitle }}</span>
          <span class="text-gray-500">{{ deterministicCollapsed ? '▸' : '▾' }}</span>
        </button>
        <div v-if="!deterministicCollapsed" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="card in insightCards" :key="`insight-card-${card.question_id}`" class="border rounded-lg bg-white p-4">
            <div class="flex items-center justify-between gap-2 mb-1">
              <div class="text-xs uppercase tracking-wide text-gray-500">{{ card.question }}</div>
              <div class="text-xs text-gray-500">Score {{ card.score }}</div>
            </div>
            <div class="text-sm font-semibold text-gray-900 mb-2">{{ card.title }}</div>
            <div class="text-sm text-gray-700">{{ card.answer }}</div>
            <div v-if="(card.evidence || []).length" class="mt-3 space-y-1">
              <div v-for="(line, lineIdx) in (card.evidence || []).slice(0, 3)" :key="`insight-card-${card.question_id}-line-${lineIdx}`" class="text-xs text-gray-500">
                {{ line }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EngagementInsightsCardsSection',
  props: {
    title: { type: String, default: 'Engagement insights' },
    keyInsightsTitle: { type: String, default: 'Key engagement insights' },
    deterministicTitle: { type: String, default: 'Deterministic insights' },
    refreshButtonLabel: { type: String, default: 'Refresh insights' },
    loadingButtonLabel: { type: String, default: 'Loading...' },
    loadingMessage: { type: String, default: 'Generating curated engagement insights...' },
    noDataMessage: { type: String, default: 'No insights available for this scope.' },
    keyInsightsEmptyMessage: { type: String, default: 'Curated insights are unavailable, deterministic findings are shown below.' },
    showRefreshButton: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    keyInsights: { type: Array, default: () => [] },
    insightCards: { type: Array, default: () => [] },
    deterministicCollapsed: { type: Boolean, default: true },
  },
  methods: {
    engagementThemeText(theme) {
      const key = String(theme || '').trim().toLowerCase()
      if (key === 'reach') return 'Reach'
      if (key === 'action') return 'Action'
      if (key === 'source') return 'Source'
      if (key === 'geo') return 'Geo'
      if (key === 'concentration') return 'Concentration'
      return key || 'Insight'
    },
  },
}
</script>
